import { fetchAuthSession } from "aws-amplify/auth";

export type AuthenticatedFetchBreadcrumb = {
  phase: "request" | "response" | "error";
  method: string;
  url: string;
  requestId: string;
  statusCode?: number;
  durationMs?: number;
  errorName?: string;
};

export type CreateAuthenticatedFetchOptions = {
  createRequestId: () => string;
  getAdditionalHeaders?: () => HeadersInit | undefined;
  onBreadcrumb?: (breadcrumb: AuthenticatedFetchBreadcrumb) => void;
};

function isRequest(input: RequestInfo | URL): input is Request {
  return typeof Request !== "undefined" && input instanceof Request;
}

function fetchMetadata(input: RequestInfo | URL, init: RequestInit | undefined) {
  const method = init?.method ?? (isRequest(input) ? input.method : "GET");
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

  return { method: method.toUpperCase(), url };
}

function headersFrom(input: RequestInfo | URL, init: RequestInit | undefined) {
  const headers = new Headers(isRequest(input) ? input.headers : undefined);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => {
      headers.set(key, value);
    });
  }
  return headers;
}

function addBreadcrumb(
  onBreadcrumb: CreateAuthenticatedFetchOptions["onBreadcrumb"],
  breadcrumb: AuthenticatedFetchBreadcrumb,
) {
  try {
    onBreadcrumb?.(breadcrumb);
  } catch {
    // Observability must never change API request behavior.
  }
}

export function createAuthenticatedFetch({
  createRequestId,
  getAdditionalHeaders,
  onBreadcrumb,
}: CreateAuthenticatedFetchOptions): typeof fetch {
  return async (input, init) => {
    const session = await fetchAuthSession();
    const token =
      session.tokens?.idToken?.toString() ??
      session.tokens?.accessToken?.toString();
    const headers = headersFrom(input, init);
    const requestId = createRequestId();
    const { method, url } = fetchMetadata(input, init);
    const startedAt = Date.now();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("X-Request-Id", requestId);
    if (getAdditionalHeaders) {
      new Headers(getAdditionalHeaders()).forEach((value, key) => {
        headers.set(key, value);
      });
    }

    addBreadcrumb(onBreadcrumb, { phase: "request", method, url, requestId });

    try {
      const response = await fetch(input, { ...init, headers });
      addBreadcrumb(onBreadcrumb, {
        phase: "response",
        method,
        url,
        requestId,
        statusCode: response.status,
        durationMs: Date.now() - startedAt,
      });
      return response;
    } catch (error) {
      addBreadcrumb(onBreadcrumb, {
        phase: "error",
        method,
        url,
        requestId,
        durationMs: Date.now() - startedAt,
        errorName: error instanceof Error ? error.name : "UnknownError",
      });
      throw error;
    }
  };
}
